import { likelihood } from "@volca/ui/trades";
import { DEFAULT_CURRENCY } from "@/profitability/currency";
import { formatSummaryValue } from "@/profitability/utils";
import type { ReviewCause, ReviewNextStep } from "./review-spec";
import type { FindingResolution, FindingStatus } from "./types";

/**
 * Why a finding was closed. `cleared` is the system's: a rerun of the period
 * stopped flagging it (`ProfitabilityFinding::SYSTEM_RESOLUTIONS`), which a
 * reviewer cannot pick.
 */
export const RESOLUTION_LABEL: Record<FindingResolution | "cleared", string> = {
  not_an_issue: "Not an issue",
  expected: "Expected for this work",
  fixed: "Fixed",
  other: "Other",
  cleared: "No longer flagged",
};

export const REASON_LABEL: Record<string, string> = {
  not_an_issue: "Not an issue",
  expected: "Expected for this work",
  wrong_numbers: "Numbers are wrong",
  already_known: "Already handled",
  unclear: "Unclear",
  useful: "Useful",
  other: "Other",
};

export const STATUS_LABEL: Record<FindingStatus, string> = {
  open: "New",
  investigating: "Investigating",
  action_required: "Action required",
  explained: "Explained",
  resolved: "Resolved",
};

export const ACTIVE_STATUSES: FindingStatus[] = [
  "open",
  "investigating",
  "action_required",
];

export const isActive = (status: FindingStatus) =>
  ACTIVE_STATUSES.includes(status);

export const money = (
  value: number | null,
  /** The report's currency (`useReportCurrency`). */
  currency: string = DEFAULT_CURRENCY,
) =>
  value === null
    ? "—"
    : formatSummaryValue({ value, type: "currency" }, currency);

export const rate = (value: number | null) =>
  value === null ? "—" : formatSummaryValue({ value, type: "percent" });

export const share = (value: number) => `${Math.round(value * 100)}%`;

const humanize = (key: string) => key.replaceAll("_", " ");

/**
 * A cause as a name, for a "Likely cause" label. The words are the API's:
 * the hypothesis' own `cause_name`, else the review's resolved cause
 * (`review.causes`), else the key in words. The app keeps no cause table.
 */
export const causeName = (
  cause: string,
  name?: string | null,
  causes: ReviewCause[] = [],
) =>
  name?.trim() ||
  causes.find((item) => item.key === cause)?.label ||
  humanize(cause);

/** An unclear cause says so, rather than a likelihood in front of "unclear". */
export const guessPhrase = (
  cause: string,
  confidence: number | undefined,
  name?: string | null,
  causes: ReviewCause[] = [],
) =>
  cause === "unclear"
    ? "Cause unclear"
    : `${likelihood(confidence)} ${causeName(cause, name, causes).toLowerCase()}`;

export const nextStepName = (
  step: string,
  label?: string | null,
  steps: ReviewNextStep[] = [],
) =>
  label?.trim() ||
  steps.find((item) => item.key === step)?.label ||
  humanize(step);
